/**
 * Единый разбор ответа сервера в человекочитаемый текст.
 *
 * Понимает форматы, которые отдаёт бэкенд:
 *   { message: "..." }                     — наши явные сообщения (409, 502)
 *   { detail: "..." }                      — DRF и заявки
 *   { error: "..." }                       — часть эндпоинтов
 *   { missing: [{field, label}, ...] }     — незаполненные поля заявки
 *   { field: ["текст"], field2: ["..."] }  — ошибки валидации сериализатора
 */
export function parseApiError(err, fallback = "Не удалось выполнить операцию") {
  // Уже разобранный объект из наших обёрток в api.js
  if (err && typeof err === "object" && err.error && !err.response) {
    return { text: err.error, kind: err.locked || err.in_use ? "warning" : "danger",
             fields: {}, firstField: null, items: [] };
  }

  // Ответ уже мог быть развёрнут вызывающим кодом (APIUpdateEvent отдаёт сам
  // axios-error, а не бросает) — поддерживаем оба варианта.
  const status = err?.response?.status;
  const d = err?.response?.data;

  if (!d) {
    return { text: err?.message || fallback, kind: "danger", fields: {}, firstField: null, items: [], status };
  }

  if (typeof d === "string") {
    return { text: d, kind: "danger", fields: {}, firstField: null, items: [], status };
  }

  // ── Единый конверт ошибок валидации (Этап 4 бэкенда) ──────────────────
  // { status:'error', detail, errors:[{field,code,message}], first_error_field }
  // Проверяем ДО ветки detail/message — иначе detail из конверта перехватит
  // ответ и потеряет разбор по полям.
  if (Array.isArray(d?.errors) && d.errors.length) {
    const fields = {};
    d.errors.forEach((it) => {
      if (!it?.field) return;
      // Вложенные пути ('additional_services.1.price') кладём и целиком,
      // и по корню — чтобы подсветить хотя бы блок целиком.
      fields[it.field] = it.message;
      const root = String(it.field).split(".")[0];
      if (!(root in fields)) fields[root] = it.message;
    });
    return {
      text: d.detail || d.errors[0]?.message || fallback,
      // Незаполненные поля — это не поломка, а подсказка: жёлтый тост, как раньше
      // отдавала ветка missing (конверт содержит оба ключа сразу).
      kind: (status === 409 || (Array.isArray(d?.missing) && d.missing.length))
        ? "warning" : "danger",
      fields,
      firstField: d.first_error_field || d.errors[0]?.field || null,
      items: d.errors,
      status,
    };
  }

  if (Array.isArray(d?.missing) && d.missing.length) {
    return {
      text: "Заполните обязательные поля: " + d.missing.map((m) => m.label).join(", "),
      kind: "warning",
      fields: Object.fromEntries(d.missing.map((m) => [m.field, m.label])),
      firstField: d.first_error_field || d.missing[0]?.field || null,
      items: d.missing.map((m) => ({ field: m.field, code: "required", message: m.label })),
      status,
    };
  }

  if (d.message || d.detail || d.error) {
    return {
      text: d.message || d.detail || d.error,
      // 409 — это не поломка, а осознанный запрет (выплата проведена и т.п.)
      kind: status === 409 ? "warning" : "danger",
      fields: {},
      firstField: d.first_error_field || null,
      items: [],
      status,
    };
  }

  // Ошибки валидации по полям («сырой» DRF, эндпоинты без конверта)
  const fields = {};
  const parts = [];
  const items = [];
  Object.entries(d).forEach(([k, v]) => {
    // Служебные ключи конверта в поля не превращаем
    if (["status", "detail", "errors", "first_error_field", "missing"].includes(k)) return;
    const text = Array.isArray(v) ? v.join(" ") : String(v);
    fields[k] = text;
    parts.push(text);
    items.push({ field: k, code: `${k}.invalid`, message: text });
  });
  if (parts.length) {
    const firstReal = items.find((it) => it.field !== "non_field_errors");
    return {
      text: parts.join(" · "),
      kind: "danger",
      fields,
      firstField: (firstReal || items[0])?.field || null,
      items,
      status,
    };
  }

  return { text: fallback, kind: "danger", fields: {}, firstField: null, items: [], status };
}



/**
 * Единый разбор ответов API в понятную ошибку.
 *
 * Сервер отвечает по-разному: {message}, {detail}, {error}, {поле: [текст]},
 * плюс бывают сетевые сбои и таймауты. Возвращаем одинаковую форму:
 *   { ok, status, code, message, fields }
 */

const NETWORK_TEXT = 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.';
const TIMEOUT_TEXT = 'Сервер долго не отвечает. Попробуйте ещё раз через минуту.';
const SERVER_TEXT  = 'Ошибка на сервере. Мы уже знаем о ней — попробуйте позже.';

const pickText = (data) => {
    if (!data) return '';
    if (typeof data === 'string') {
        // HTML от nginx (413/502) — текст пользователю не показываем
        return data.trim().startsWith('<') ? '' : data.trim();
    }
    if (data.detail) return String(data.detail);
    if (data.message) return String(data.message);
    if (data.error) return String(data.error);

    // Ошибки сериализатора DRF: {"email": ["Уже используется"]}
    const firstKey = Object.keys(data)[0];
    if (firstKey) {
        const value = data[firstKey];
        if (Array.isArray(value) && value.length) return String(value[0]);
        if (typeof value === 'string') return value;
    }
    return '';
};

const pickFields = (data) => {
    if (!data || typeof data !== 'object') return {};
    if (data.fields && typeof data.fields === 'object') return data.fields;

    const out = {};
    Object.entries(data).forEach(([key, value]) => {
        if (['detail', 'message', 'error', 'status', 'code', 'errors'].includes(key)) return;
        if (Array.isArray(value) && value.length) out[key] = String(value[0]);
        else if (typeof value === 'string') out[key] = value;
    });
    return out;
};

/** Ответ axios/fetch → { ok:false, status, code, message, fields } */
export const normalizeApiError = (error, fallback = 'Не удалось выполнить запрос') => {
    // Уже нормализованный объект
    if (error && error.ok === false && error.message) return error;

    const status = error?.response?.status || error?.status || 0;
    const data = error?.response?.data ?? error?.data ?? null;

    let message = pickText(data);
    let code = (data && typeof data === 'object' && data.code) || '';

    if (!message) {
        if (error?.code === 'ECONNABORTED') { message = TIMEOUT_TEXT; code = code || 'timeout'; }
        else if (error?.code === 'ERR_NETWORK' || status === 0) { message = NETWORK_TEXT; code = code || 'network'; }
        else if (status === 413) { message = 'Файл слишком большой для отправки.'; code = code || 'too_large'; }
        else if (status === 401) { message = 'Неверные данные для входа.'; code = code || 'unauthorized'; }
        else if (status === 403) { message = 'Доступ запрещён.'; code = code || 'forbidden'; }
        else if (status === 404) { message = 'Не найдено. Проверьте введённые данные.'; code = code || 'not_found'; }
        else if (status === 429) { message = 'Слишком часто. Подождите немного.'; code = code || 'rate_limit'; }
        else if (status >= 500) { message = SERVER_TEXT; code = code || 'server_error'; }
        else message = fallback;
    }

    return {
        ok: false,
        status,
        code,
        message,
        fields: pickFields(data),
        retry_after: (data && data.retry_after) || null,
    };
};

/** Успешный ответ приводим к общей форме */
export const okResult = (data) => ({ ok: true, ...(data || {}) });

/** true, если объект — нормализованная ошибка */
export const isApiError = (value) => !!value && value.ok === false;